import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useTailoringRoute } from "@/context/TailoringModeContext";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { Separator } from "@/components/ui/separator";
import { ArrowLeft, Building, Target, CheckCircle, Zap, Download, X } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import jsPDF from 'jspdf';
import html2canvas from 'html2canvas';
import { supabase } from "@/integrations/supabase/client";

interface JobAnalysisDetailProps {
  job: SavedJobDescription;
  onClose: () => void;
}

interface SavedJobDescription {
  id: string;
  companyName: string;
  jobTitle: string;
  companyMission: string;
  selectedResponsibilities: string[];
  selectedRequirements: string[];
  selectedSkills: string[];
  allResponsibilities: string[];
  allRequirements: string[];
  allSkills: string[];
  matchScore: number;
  dateCreated: string;
}

export function JobAnalysisDetail({ job, onClose }: JobAnalysisDetailProps) {
  const { toast } = useToast();
  const navigate = useNavigate();
  const { route: tailoringRoute } = useTailoringRoute();
  const [userProfile, setUserProfile] = useState<any>(null);

  // Get user profile for personalization
  useEffect(() => {
    const fetchUserProfile = async () => {
      const { data: { user } } = await supabase.auth.getUser();
      if (user) {
        const { data: profile } = await supabase
          .from('profiles')
          .select('first_name, display_name')
          .eq('id', user.id)
          .single();
        
        setUserProfile(profile);
      }
    };
    
    fetchUserProfile();
  }, []);

  // Calculate individual scores (percentages for display)
  const skillsScore = job.allSkills.length > 0 ? (job.selectedSkills.length / job.allSkills.length) : 0;
  const responsibilitiesScore = job.allResponsibilities.length > 0 ? (job.selectedResponsibilities.length / job.allResponsibilities.length) : 0;
  const requirementsScore = job.allRequirements.length > 0 ? (job.selectedRequirements.length / job.allRequirements.length) : 0;

  const getScoreColor = (score: number) => {
    if (score >= 86) return "text-green-600";
    if (score >= 70) return "text-yellow-600"; 
    if (score >= 31) return "text-orange-600";
    return "text-red-600";
  };

  const getScoreLabel = (score: number) => {
    if (score >= 86) return "Excellent Match";
    if (score >= 70) return "Good Match";
    if (score >= 31) return "Fair Match";  
    return "Poor Match";
  };

  const getQualificationStatus = (score: number) => {
    return score >= 70 ? "Congrats! You are Qualified to Apply for This Job!" : "You are NOT Qualified to Apply for This Job.";
  };

  const getFeedbackMessage = (score: number, clientName = "") => {
    if (score >= 86) {
      return `🟢 Great news, ${clientName}! Your qualifications and experience closely align with the requirements of the ${job.jobTitle} at ${job.companyName}. You are a strong match for this position, and we highly recommend that you apply for it as soon as possible. Your background and skills make you an ideal candidate for the role, and you have a great chance of standing out among other applicants. Don't hesitate to showcase your qualifications and experiences in your application, as you are a top contender for the position.`;
    }
    if (score >= 70) {
      return `🟡 Congratulations, ${clientName}! You have several qualifications and experience that align well with the requirements of the ${job.jobTitle} at ${job.companyName}. While you are not a perfect match, you have a good chance of being considered for the position. We recommend applying for this job, as you are a promising candidate. Emphasize the relevant qualifications and experience you possess in your application to highlight your suitability for the role.`;
    }
    if (score >= 31) {
      return `🟠 Hello ${clientName}, your match score suggests that you currently do not meet enough of the qualifications and experience required for the ${job.jobTitle} at ${job.companyName}. We advise against applying for the position at this time. However, you do have some relevant qualifications and experience, and there is potential for improvement. Consider further developing your skills and gaining additional experience that align with the job requirements. This will enhance your candidacy for similar roles in the future.`;
    }
    return `🟥 Dear ${clientName}, based on our analysis, your current qualifications and experience do not align with the requirements of the ${job.jobTitle} at ${job.companyName}. At this time, you do not qualify for this position. We recommend focusing on gaining additional relevant qualifications, acquiring new skills, or gaining more experience to improve your candidacy for similar positions in the future. It's best not to apply for this job right now, but with further development, you can increase your chances for future opportunities.`;
  };

  const getExcludedItems = () => {
    const excludedResponsibilities = job.allResponsibilities.filter(r => !job.selectedResponsibilities.includes(r));
    const excludedRequirements = job.allRequirements.filter(r => !job.selectedRequirements.includes(r));
    const excludedSkills = job.allSkills.filter(s => !job.selectedSkills.includes(s));
    
    return {
      responsibilities: excludedResponsibilities,
      requirements: excludedRequirements,
      skills: excludedSkills
    };
  };

  const generatePDFReport = async () => {
    try {
      const clientName = userProfile?.first_name || userProfile?.display_name || "";
      
      toast({
        title: "Generating Report",
        description: "Please wait while we create your PDF report..."
      });
      
      // Find the main content area (everything inside CardContent)
      const element = document.querySelector('[data-pdf-content]') as HTMLElement;
      
      if (!element) {
        throw new Error('Report content not found');
      }
      
      // Configure html2canvas options for better quality
      const canvas = await html2canvas(element, {
        scale: 2, // Higher resolution
        useCORS: true,
        backgroundColor: '#ffffff',
        scrollX: 0,
        scrollY: 0,
        windowWidth: element.scrollWidth,
        windowHeight: element.scrollHeight
      });
      
      // Create PDF
      const imgData = canvas.toDataURL('image/png');
      const pdf = new jsPDF('p', 'mm', 'a4');
      
      // Calculate dimensions
      const imgWidth = 210; // A4 width in mm
      const pageHeight = 295; // A4 height in mm
      const imgHeight = (canvas.height * imgWidth) / canvas.width;
      let heightLeft = imgHeight;
      let position = 0;
      
      // Add first page
      pdf.addImage(imgData, 'PNG', 0, position, imgWidth, imgHeight);
      heightLeft -= pageHeight;
      
      // Add additional pages if needed
      while (heightLeft >= 0) {
        position = heightLeft - imgHeight;
        pdf.addPage();
        pdf.addImage(imgData, 'PNG', 0, position, imgWidth, imgHeight);
        heightLeft -= pageHeight;
      }
      
      // Save the PDF
      pdf.save(`Job_Analysis_Report_${job.companyName}_${job.jobTitle.replace(/\s+/g, '_')}.pdf`);
      
      toast({
        title: "Report Downloaded",
        description: "Your detailed job qualifications analysis report has been saved as a PDF."
      });
    } catch (error) {
      console.error('Error generating PDF:', error);
      toast({
        title: "PDF Generation Failed",
        description: "There was an error generating your PDF report. Please try again.",
        variant: "destructive"
      });
    }
  };

  const excluded = getExcludedItems();
  const totalItems = job.allResponsibilities.length + job.allRequirements.length + job.allSkills.length;
  const selectedItems = job.selectedResponsibilities.length + job.selectedRequirements.length + job.selectedSkills.length;

  return (
    <div className="fixed inset-0 bg-background/80 backdrop-blur-sm z-50 flex items-center justify-center p-4">
      <Card className="w-full max-w-6xl max-h-[90vh] overflow-y-auto">
        <CardHeader className="border-b border-border-light">
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="flex items-center gap-2">
                <Target className="w-5 h-5 text-success" />
                Job Qualifications Analysis
              </CardTitle>
              <CardDescription>
                Detailed analysis for {job.jobTitle} at {job.companyName}
              </CardDescription>
            </div>
            <Button variant="ghost" onClick={onClose}>
              <X className="w-4 h-4" />
            </Button>
          </div>
          <Progress value={100} className="w-full mt-4" />
        </CardHeader>

        <CardContent className="p-6" data-pdf-content>
          <div className="space-y-6">
            {/* Qualification Match Score */}
            <Card className="bg-gradient-to-r from-primary/10 to-accent/10 border-primary/20">
              <CardContent className="p-6">
                <div className="text-center">
                  <h2 className="text-2xl font-bold mb-4 flex items-center justify-center gap-2">
                    <Target className="w-6 h-6 text-primary" />
                    Qualification Match Score
                  </h2>
                  <div className="flex items-center justify-center gap-6">
                    <div className={`text-5xl font-bold ${getScoreColor(job.matchScore)}`}>
                      {job.matchScore}%
                    </div>
                    <div className="text-left">
                      <p className={`text-xl font-semibold ${getScoreColor(job.matchScore)}`}>
                        {getScoreLabel(job.matchScore)}
                      </p>
                      <p className="text-muted-foreground">
                        {selectedItems} of {totalItems} qualifications selected
                      </p>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Qualification Status */}
            <Card className={`border ${job.matchScore >= 70 ? 'border-green-500 bg-green-50' : 'border-red-500 bg-red-50'}`}>
              <CardContent className="p-6 text-center">
                <h3 className={`text-xl font-bold mb-2 ${job.matchScore >= 70 ? 'text-green-600' : 'text-red-600'}`}>
                  {getQualificationStatus(job.matchScore)}
                </h3>
                {job.matchScore < 70 && (
                  <div className="mt-3">
                    <Button 
                      onClick={() => navigate(tailoringRoute)}
                      className="bg-primary hover:bg-primary/90"
                    >
                      Use TailorWiz to Tailor it Now
                    </Button>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Individual Scores Breakdown */}
            <Card className="bg-gradient-to-r from-background to-background-secondary border-border-light">
              <CardContent className="p-6">
                <h3 className="text-xl font-bold mb-4 flex items-center gap-2">
                  📈 Individual Scores Breakdown
                </h3>
                <div className="grid md:grid-cols-3 gap-4">
                  <div className="p-4 rounded-lg bg-primary/10 border border-primary/20">
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-sm font-medium">🛠 Skills (20%)</span>
                      <span className="text-lg font-bold text-primary">{Math.round(skillsScore * 100)}%</span>
                    </div>
                    <p className="text-sm text-muted-foreground">{job.selectedSkills.length} out of {job.allSkills.length} skills</p>
                  </div>
                  <div className="p-4 rounded-lg bg-accent/10 border border-accent/20">
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-sm font-medium">💡 Abilities (30%)</span>
                      <span className="text-lg font-bold text-accent">{Math.round(responsibilitiesScore * 100)}%</span>
                    </div>
                    <p className="text-sm text-muted-foreground">{job.selectedResponsibilities.length} out of {job.allResponsibilities.length} abilities</p>
                  </div>
                  <div className="p-4 rounded-lg bg-success/10 border border-success/20">
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-sm font-medium">✔ Requirements (50%)</span>
                      <span className="text-lg font-bold text-success">{Math.round(requirementsScore * 100)}%</span>
                    </div>
                    <p className="text-sm text-muted-foreground">{job.selectedRequirements.length} out of {job.allRequirements.length} requirements</p>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Your Fit for This Role */}
            <Card className="bg-gradient-to-r from-background to-background-secondary border-border-light">
              <CardContent className="p-6">
                <h3 className="text-xl font-bold mb-4 flex items-center gap-2">
                  🎯 Your Fit for This Role:
                </h3>
                <div className="prose prose-sm max-w-none">
                  <p className="text-base leading-relaxed">
                    {getFeedbackMessage(job.matchScore, userProfile?.first_name || userProfile?.display_name || "Congratulations")}
                  </p>
                </div>
              </CardContent>
            </Card>

            {/* Areas for Improvement */}
            {(excluded.responsibilities.length > 0 || excluded.skills.length > 0 || excluded.requirements.length > 0) && (
              <Card className="bg-gradient-to-r from-orange-50 to-yellow-50 border-orange-200">
                <CardContent className="p-6">
                  <h3 className="text-xl font-bold mb-4 flex items-center gap-2 text-orange-600">
                    ⚙ Top Areas for Improvement
                  </h3>
                  
                  <div className="space-y-6">
                    {excluded.responsibilities.length > 0 && (
                      <div>
                        <h4 className="font-semibold mb-3 text-orange-700">💼 Abilities/Responsibilities/Experiences (Missing or Needs Improvement):</h4>
                        <div className="space-y-2">
                          {excluded.responsibilities.slice(0, 3).map((item, index) => (
                            <div key={index} className="p-3 bg-white rounded-lg border border-orange-200">
                              <p className="text-sm">
                                <span className="font-medium">{index + 1}. {item}</span> – This responsibility is crucial for successful job performance as it demonstrates your ability to handle core job functions. Consider gaining experience through internships, volunteer work, or professional development programs that focus on this area.
                              </p>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {excluded.skills.length > 0 && (
                      <div>
                        <h4 className="font-semibold mb-3 text-orange-700">🧠 Skills (Missing or Needs Improvement):</h4>
                        <div className="space-y-2">
                          {excluded.skills.slice(0, 3).map((item, index) => (
                            <div key={index} className="p-3 bg-white rounded-lg border border-orange-200">
                              <p className="text-sm">
                                <span className="font-medium">{index + 1}. {item}</span> – This skill is critical for job success and daily operations. We recommend pursuing online courses, certifications, workshops, or hands-on training to develop proficiency in this area.
                              </p>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {excluded.requirements.length > 0 && (
                      <div>
                        <h4 className="font-semibold mb-3 text-orange-700">📋 Requirements (Missing or Needs Improvement):</h4>
                        <div className="space-y-2">
                          {excluded.requirements.slice(0, 3).map((item, index) => (
                            <div key={index} className="p-3 bg-white rounded-lg border border-orange-200">
                              <p className="text-sm">
                                <span className="font-medium">{index + 1}. {item}</span> – This requirement is essential for position eligibility and demonstrates the baseline qualifications employers expect. Focus on meeting this requirement through formal education, certification programs, or relevant work experience.
                              </p>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Tool Recommendations */}
            <div className="grid md:grid-cols-3 gap-6">
              {/* Find Your Hiring Manager */}
              <Card className="bg-gradient-to-br from-primary/10 to-primary/5 border-primary/20 cursor-pointer hover:shadow-lg transition-all duration-200">
                <CardContent className="p-6">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="w-10 h-10 bg-primary rounded-lg flex items-center justify-center">
                      <Target className="w-5 h-5 text-white" />
                    </div>
                    <h3 className="text-lg font-semibold text-primary">Find Your Hiring Manager</h3>
                  </div>
                  <p className="text-sm text-muted-foreground leading-relaxed">
                    Locate the decision-maker for any role so you can bypass HR/ATS and connect directly with the person who makes the hiring decisions.
                  </p>
                </CardContent>
              </Card>

              {/* Career Clarity Tool */}
              <Card className="bg-gradient-to-br from-accent/10 to-accent/5 border-accent/20 cursor-pointer hover:shadow-lg transition-all duration-200">
                <CardContent className="p-6">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="w-10 h-10 bg-accent rounded-lg flex items-center justify-center">
                      <CheckCircle className="w-5 h-5 text-white" />
                    </div>
                    <h3 className="text-lg font-semibold text-accent">Career Clarity Tool</h3>
                  </div>
                  <p className="text-sm text-muted-foreground leading-relaxed">
                    Discover the careers and industries that best match your skills, experience, and goals, so you know exactly where to focus your job search.
                  </p>
                </CardContent>
              </Card>

              {/* Job Search & Tracking Hub */}
              <Card className="bg-gradient-to-br from-success/10 to-success/5 border-success/20 cursor-pointer hover:shadow-lg transition-all duration-200">
                <CardContent className="p-6">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="w-10 h-10 bg-success rounded-lg flex items-center justify-center">
                      <Zap className="w-5 h-5 text-white" />
                    </div>
                    <h3 className="text-lg font-semibold text-success">Advanced Job Finder & Tracking Hub</h3>
                  </div>
                  <p className="text-sm text-muted-foreground leading-relaxed">
                    Search, organize, and track job opportunities in one place to stay on top of applications and follow-ups without missing deadlines.
                  </p>
                </CardContent>
              </Card>
            </div>

            {/* Action Buttons */}
            <div className="flex justify-between pt-4 border-t border-border">
              <Button variant="outline" onClick={onClose}>
                <ArrowLeft className="w-4 h-4" />
                Back to Library
              </Button>
              <Button 
                variant="outline" 
                onClick={generatePDFReport}
                size="lg"
              >
                <Download className="w-4 h-4" />
                Download Report
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}